// Zod validator for an achievement's metric-based unlock rules. Lives in `core`
// because both achievement modules (bireysel + takım) share it — each passes
// its own metric catalog so "kategori zorunlu" / operator support is validated
// against the metrics that module actually offers.

import { z } from 'zod'

import {
  findAchievementMetric,
  unlockValuePair,
  type TAchievementMetricDefinition,
} from '@/core/utils/achievement-unlock'

const errors = {
  metric: 'Geçerli bir koşul metriği seçilmelidir',
  operator: 'Bu metrik için geçersiz karşılaştırma seçildi',
  category: 'Bu koşul için kategori seçilmesi zorunludur',
  valueMin: 'Eşik değeri 0’dan büyük olmalıdır',
  valuePercentage: 'Yüzde değeri 1 ile 100 arasında olmalıdır',
  rangeOrder: 'Üst sınır, alt sınırdan büyük olmalıdır',
}

const unlockRuleSchema = z.object({
  id: z.string().min(1),
  metric: z.string().min(1, errors.metric),
  category_id: z.string().nullable(),
  operator: z.enum(['gte', 'lte', 'eq', 'between']),
  value: z.union([z.number(), z.tuple([z.number(), z.number()])]),
})

/**
 * Builds the `unlock_rules` array validator for a given metric catalog.
 * Issues are reported per rule index so react-hook-form can surface them inline
 * on the offending row (`unlock_rules.0.category_id`, …).
 */
export function buildAchievementUnlockRulesSchema(
  catalog: readonly TAchievementMetricDefinition[]
) {
  return z.array(unlockRuleSchema).superRefine((rules, ctx) => {
    rules.forEach((rule, index) => {
      const definition = findAchievementMetric(catalog, rule.metric)
      if (!definition) {
        ctx.addIssue({
          code: 'custom',
          message: errors.metric,
          path: [index, 'metric'],
        })
        return
      }

      if (!definition.operators.includes(rule.operator)) {
        ctx.addIssue({
          code: 'custom',
          message: errors.operator,
          path: [index, 'operator'],
        })
      }

      if (definition.requires_category && !rule.category_id) {
        ctx.addIssue({
          code: 'custom',
          message: errors.category,
          path: [index, 'category_id'],
        })
      }

      const isPercentage = definition.value_type === 'percentage'
      const [min, max] = unlockValuePair(rule.value)
      const bounds = rule.operator === 'between' ? [min, max] : [min]

      for (const bound of bounds) {
        if (!Number.isFinite(bound) || bound <= 0) {
          ctx.addIssue({
            code: 'custom',
            message: errors.valueMin,
            path: [index, 'value'],
          })
          return
        }
        if (isPercentage && bound > 100) {
          ctx.addIssue({
            code: 'custom',
            message: errors.valuePercentage,
            path: [index, 'value'],
          })
          return
        }
      }

      if (rule.operator === 'between' && max <= min) {
        ctx.addIssue({
          code: 'custom',
          message: errors.rangeOrder,
          path: [index, 'value'],
        })
      }
    })
  })
}

export const achievementUnlockRulesErrors = errors
